import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';

const MojeTimbrature = ({ userId }) => {
  const { t, i18n } = useTranslation();
  const [presenze, setPresenze] = useState([]);
  const [workerInfo, setWorkerInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [filterMese, setFilterMese] = useState(new Date().getMonth() + 1);
  const [filterAnno, setFilterAnno] = useState(new Date().getFullYear());

  // Dinamički nazivi mjeseci na osnovu prevoda
const mjeseci = [
  { n: 1, ime: t('months.Gennaio') }, 
  { n: 2, ime: t('months.Febbraio') }, 
  { n: 3, ime: t('months.Marzo') },
  { n: 4, ime: t('months.Aprile') }, 
  { n: 5, ime: t('months.Maggio') }, 
  { n: 6, ime: t('months.Giugno') },
  { n: 7, ime: t('months.Luglio') }, 
  { n: 8, ime: t('months.Agosto') }, 
  { n: 9, ime: t('months.Settembre') },
  { n: 10, ime: t('months.Ottobre') }, 
  { n: 11, ime: t('months.Novembre') }, 
  { n: 12, ime: t('months.Dicembre') }
];

  const formatHMin = (decimalHours) => {
    if (!decimalHours || decimalHours <= 0) return "0h 0m";
    const totalMinutes = Math.round(decimalHours * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h ${m}m`;
  };

  const fetchPresenze = async () => {
    if (!userId) return;
    setLoading(true);
    setShow(true);
    try {
      const { data: profil, error: profilErr } = await supabase
        .from('profili')
        .select('nome, cognome')
        .eq('id', userId)
        .single();
      
      if (profilErr) console.error("Profil error:", profilErr);
      setWorkerInfo(profil);

      const startDate = new Date(filterAnno, filterMese - 1, 1).toISOString();
      const endDate = new Date(filterAnno, filterMese, 0, 23, 59, 59).toISOString();

      const { data, error } = await supabase
        .from('presenze')
        .select('*')
        .eq('id_utente', userId)
        .gte('orario_ingresso', startDate)
        .lte('orario_ingresso', endDate)
        .order('orario_ingresso', { ascending: false });

      if (error) throw error;
      setPresenze(data || []);
    } catch (err) {
      console.error("Errore:", err.message);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    let ordTotal = 0;
    let straTotal = 0;
    presenze.forEach(p => {
      if (p.orario_ingresso && p.orario_uscita) {
        const diff = (new Date(p.orario_uscita) - new Date(p.orario_ingresso)) / (1000 * 60 * 60);
        if (diff > 8) {
          ordTotal += 8;
          straTotal += (diff - 8);
        } else {
          ordTotal += diff;
        }
      }
    });
    return { ordTotal, straTotal, total: ordTotal + straTotal };
  };

  const totals = calculateTotals();

  const exportToExcel = () => {
    const nome = workerInfo?.nome || 'Nome';
    const cognome = workerInfo?.cognome || 'Cognome';
    const meseIme = mjeseci.find(m => m.n === parseInt(filterMese))?.ime || filterMese;

    const reportData = presenze.map(p => {
      const diff = p.orario_uscita ? (new Date(p.orario_uscita) - new Date(p.orario_ingresso)) / (1000 * 60 * 60) : 0;
      const ord = diff > 8 ? 8 : diff;
      const stra = diff > 8 ? diff - 8 : 0;
      return {
        [t('timbrature.table.name')]: nome,
        [t('timbrature.table.surname')]: cognome,
        [t('timbrature.table.date')]: new Date(p.orario_ingresso).toLocaleDateString(i18n.language),
        [t('timbrature.table.entry')]: new Date(p.orario_ingresso).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }),
        [t('timbrature.table.exit')]: p.orario_uscita ? new Date(p.orario_uscita).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' }) : '-',
        [t('timbrature.stats.ord')]: formatHMin(ord),
        [t('timbrature.stats.straord')]: stra > 0 ? formatHMin(stra) : '0h 0m',
        [t('timbrature.stats.total')]: formatHMin(diff),
        'Note': p.note || ''
      };
    });

    const ws = XLSX.utils.json_to_sheet(reportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('timbrature.archive_title'));
    
    const fileName = `presenze_${nome.toLowerCase()}${cognome.toLowerCase()}_${meseIme.toLowerCase()}_${filterAnno}.xlsx`.replace(/\s/g, '');
    XLSX.writeFile(wb, fileName);
  };

  return (
    <div className="space-y-4 mt-12">
      <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="p-4 border-b border-border bg-muted/10">
          <h3 className="font-bold flex items-center gap-2 uppercase text-xs tracking-widest text-muted-foreground">
            <Icon name="Search" size={16} className="text-primary" /> 
            {t('timbrature.archive_title')}
          </h3>
        </div>
        
        <div className="p-4 flex flex-col md:flex-row gap-4 items-end">
          <div className="flex-1 w-full grid grid-cols-2 md:grid-cols-3 gap-2">
<div>
  <label className="text-[10px] font-black uppercase text-muted-foreground mb-1 block ml-1 tracking-widest">
    {t('timbrature.filters.month')}
  </label>
  <select 
    className="w-full p-2.5 bg-muted/30 border border-border rounded-xl text-sm font-bold outline-none" 
    value={filterMese} 
    onChange={(e) => setFilterMese(parseInt(e.target.value))}
  >
    {mjeseci.map(m => (
      <option key={m.n} value={m.n}>
        {m.ime}
      </option>
    ))}
  </select>
</div>
            <div>
              <label className="text-[10px] font-black uppercase text-muted-foreground mb-1 block ml-1 tracking-widest">{t('timbrature.filters.year')}</label>
              <input type="number" className="w-full p-2.5 bg-muted/30 border border-border rounded-xl text-sm font-bold outline-none" value={filterAnno} onChange={(e) => setFilterAnno(e.target.value)} />
            </div>
            <div className="col-span-2 md:col-span-1">
              <button onClick={fetchPresenze} className="w-full bg-primary text-white p-2.5 rounded-xl text-sm font-black uppercase tracking-widest flex items-center justify-center gap-2 hover:opacity-90 transition-all shadow-lg shadow-primary/20">
                <Icon name={loading ? "Loader2" : "RefreshCw"} size={16} className={loading ? "animate-spin" : ""} />
                {loading ? t('timbrature.filters.loading') : t('timbrature.filters.view')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {show && (
        <div className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-500">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-card border border-border p-5 rounded-2xl shadow-sm">
              <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1">{t('timbrature.stats.ord')}</p>
              <p className="text-2xl font-black text-emerald-600">{formatHMin(totals.ordTotal)}</p>
            </div>
            <div className="bg-card border border-border p-5 rounded-2xl shadow-sm">
              <p className="text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-1">{t('timbrature.stats.straord')}</p>
              <p className="text-2xl font-black text-orange-500">{formatHMin(totals.straTotal)}</p>
            </div>
            <div className="bg-primary/5 border border-primary/20 p-5 rounded-2xl shadow-sm">
              <p className="text-[10px] font-black text-primary uppercase tracking-widest mb-1">{t('timbrature.stats.monthly_total')}</p>
              <p className="text-2xl font-black text-primary">{formatHMin(totals.total)}</p>
            </div>
          </div>

          <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden text-[13px]">
            <div className="p-4 border-b border-border bg-muted/10 flex justify-between items-center">
              <h3 className="font-bold flex items-center gap-2 uppercase text-xs tracking-widest text-muted-foreground">
                <Icon name="Clock" size={16} className="text-primary" /> {t('timbrature.history_title')}
              </h3>
              {presenze.length > 0 && (
                <button onClick={exportToExcel} className="flex items-center gap-2 text-[10px] font-black uppercase bg-emerald-600 text-white px-3 py-1.5 rounded-lg hover:bg-emerald-700 transition-all">
                  <Icon name="FileSpreadsheet" size={14} /> Export Excel
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead className="bg-muted/30 text-muted-foreground border-b border-border">
                  <tr>
                    <th className="p-4 font-bold uppercase text-[10px] tracking-widest">{t('timbrature.table.date')}</th>
                    <th className="p-4 font-bold uppercase text-[10px] tracking-widest">{t('timbrature.table.entry')}</th>
                    <th className="p-4 font-bold uppercase text-[10px] tracking-widest">{t('timbrature.table.exit')}</th>
                    <th className="p-4 font-bold uppercase text-[10px] tracking-widest text-emerald-700">{t('timbrature.table.ord_short')}</th>
                    <th className="p-4 font-bold uppercase text-[10px] tracking-widest text-orange-600">{t('timbrature.table.straord_short')}</th>
                    <th className="p-4 font-bold uppercase text-[10px] tracking-widest bg-muted/5">{t('timbrature.stats.total')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {presenze.length > 0 ? (
                    presenze.map(p => {
                      const inTime = new Date(p.orario_ingresso);
                      const outTime = p.orario_uscita ? new Date(p.orario_uscita) : null;
                      const diff = outTime ? (outTime - inTime) / (1000 * 60 * 60) : 0;
                      const ord = diff > 8 ? 8 : diff;
                      const stra = diff > 8 ? diff - 8 : 0;
                      return (
                        <tr key={p.id} className="hover:bg-muted/5 transition-colors">
                          <td className="p-4 font-bold">{inTime.toLocaleDateString(i18n.language)}</td>
                          <td className="p-4 text-emerald-600 font-medium">
                            {inTime.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                          </td>
                          <td className="p-4 text-orange-600 font-medium">
                            {outTime 
                              ? outTime.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit', second: '2-digit' }) 
                              : '--:--:--'}
                          </td>
                          <td className="p-4 font-bold text-emerald-700">{formatHMin(ord)}</td>
                          <td className="p-4 font-bold text-orange-600">{stra > 0 ? formatHMin(stra) : '-'}</td>
                          <td className="p-4 font-black text-lg bg-muted/10 text-primary">{formatHMin(diff)}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="6" className="p-12 text-center text-muted-foreground italic tracking-widest uppercase text-[11px]">
                        {t('timbrature.no_data')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MojeTimbrature;